import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Button,
  DatePicker,
  Divider,
  Modal,
  Radio,
  Switch,
  Tooltip,
} from 'antd';
import { isEmpty } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../AppContext';
import { ReactComponent as ArchiveIcon } from '../../../assets/svg/box-archive-solid.svg';
import { ReactComponent as ViewIcon } from '../../../assets/svg/eye-solid.svg';
import { ReactComponent as CopyLinkIcon } from '../../../assets/svg/link-solid.svg';
import { ReactComponent as EditIcon } from '../../../assets/svg/pen-to-square-solid.svg';
import { ReactComponent as UnarchiveIcon } from '../../../assets/svg/right-from-bracket-solid.svg';
import {
  ARCHIVING_STATE,
  BREAKPOINTS,
  DATE_FORMATS,
  LIMIT,
  MEDIA_LIBRARY_SORT_FIELDS,
  PERMISSIONS_KEY,
  PERMISSION_TYPE,
  SORT,
} from '../../../common/constants';
import {
  copyToClipboard,
  formatDate,
  formatFileSize,
  hasPermission,
} from '../../../common/utils';
import CommonTable from '../../../components/CommonTable';
import SearchComponent from '../../../components/SearchComponent';
import { UPDATE_MEDIA_UPLOAD } from '../graphql/Mutations';
import { MEDIA_UPLOADS } from '../graphql/Queries';
import useMediaUploadOptions from '../hooks/useMediaUploadOptions';
import EditMediaModal from './EditMediaModal';
import UploadMediaModal from './UploadMediaModal';

const { Group } = Radio;
const { RangePicker } = DatePicker;

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: LIMIT,
};

const initialFilter = {
  limit: LIMIT,
  search: '',
  skip: 0,
};

const initialSort = {
  field: MEDIA_LIBRARY_SORT_FIELDS?.CREATED_AT,
  order: SORT?.DESC,
};

const MediaLibraryTable = ({ isUploadModalOpen, setIsUploadModalOpen }) => {
  const {
    state: { permissions },
  } = useContext(AppContext);
  const {
    isOptionsLoading,
    typeOptions,
    fileTypeOptions,
    allSubtypeOptions,
    getSubtypeOptions,
    typeLabels,
    subtypeLabels,
    fileTypeLabels,
  } = useMediaUploadOptions();

  const [mediaList, setMediaList] = useState([]);
  const [mediaData, setMediaData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [query, setQuery] = useState('');
  const [sortedInfo, setSortedInfo] = useState({});
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [isMediaLoading, setIsMediaLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditBtnLoading, setIsEditBtnLoading] = useState(false);
  const [isArchivePrompt, setIsArchivePrompt] = useState(false);
  const [isArchiveBtnLoading, setIsArchiveBtnLoading] = useState(false);
  const [isArchiveFilter, setIsArchiveFilter] = useState(false);
  const [whereFilter, setWhereFilter] = useState();
  const [finalWhereFilters, setFinalWhereFilters] = useState();
  const [filterVisible, setFilterVisible] = useState(false);

  const hasUpdatePermission = hasPermission(
    PERMISSIONS_KEY?.MASTER_LIST_MEDIA_UPLOAD,
    PERMISSION_TYPE?.UPDATE,
    permissions,
  );

  const [mediaUploads] = useLazyQuery(MEDIA_UPLOADS, {
    fetchPolicy: 'network-only',
    onCompleted: (response) => {
      setMediaList([...(response?.mediaUploads?.data || [])]);
      setPaginationProp((prev) => ({
        ...prev,
        total: response?.mediaUploads?.count || 0,
      }));
      setIsMediaLoading(false);
    },
    onError: () => setIsMediaLoading(false),
  });

  const [updateMediaUpload] = useMutation(UPDATE_MEDIA_UPLOAD, {
    onError: () => {},
  });

  const fetchMediaList = (overrides = {}) => {
    const {
      filters = finalWhereFilters,
      pagination = paginationProp,
      sorter = sortedInfo,
      search = searchTerm,
      archived = isArchiveFilter,
    } = overrides;
    setIsMediaLoading(true);
    mediaUploads({
      variables: {
        filter: {
          ...initialFilter,
          skip:
            ((pagination?.current || 1) - 1) * (pagination?.pageSize || LIMIT),
          limit: pagination?.pageSize || LIMIT,
          search,
          isArchived: archived,
          type: filters?.type || null,
          subtype: filters?.subtype || null,
          fileType: filters?.fileType || null,
          dateRange: {
            from: filters?.createdAt?.from?.$d || null,
            to: filters?.createdAt?.to?.$d || null,
          },
        },
        sort: sorter?.column
          ? {
              field: sorter?.field,
              order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialSort,
      },
    });
  };

  useEffect(() => {
    const pagination = {
      ...initialPaginationValue,
      pageSize: paginationProp?.pageSize,
    };
    setPaginationProp(pagination);
    fetchMediaList({ pagination, archived: isArchiveFilter });
  }, [isArchiveFilter]);

  const handleTableChange = (pagination, tableFilter, sorter) => {
    setPaginationProp({ ...paginationProp, ...pagination });
    setSortedInfo(sorter);
    fetchMediaList({ pagination, sorter });
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setSearchTerm(trimValue);
    const pagination = { ...paginationProp, current: 1 };
    setPaginationProp(pagination);
    fetchMediaList({ search: trimValue, pagination });
  };

  const refetchCurrentPage = () => fetchMediaList();

  // Step back a page when the row just removed was the only one on this page
  const getPaginationAfterRowRemoved = () => {
    const isLastRowOnPage =
      mediaList?.length === 1 && paginationProp?.current > 1;
    if (!isLastRowOnPage) return paginationProp;
    const pagination = {
      ...paginationProp,
      current: paginationProp?.current - 1,
    };
    setPaginationProp(pagination);
    return pagination;
  };

  const handleEdit = async (values) => {
    setIsEditBtnLoading(true);
    const response = await updateMediaUpload({
      variables: {
        where: { id: mediaData?.id },
        data: {
          name: values?.name?.trim(),
          type: values?.type,
          subtype: values?.subtype || null,
        },
      },
    });
    if (response?.data) {
      setIsEditModalOpen(false);
      setMediaData(null);
      refetchCurrentPage();
    }
    setIsEditBtnLoading(false);
  };

  const handleArchive = async () => {
    setIsArchiveBtnLoading(true);
    const response = await updateMediaUpload({
      variables: {
        where: { id: mediaData?.id },
        data: { isArchived: !mediaData?.isArchived },
      },
    });
    if (response?.data) {
      setIsArchivePrompt(false);
      setMediaData(null);
      fetchMediaList({ pagination: getPaginationAfterRowRemoved() });
    }
    setIsArchiveBtnLoading(false);
  };

  const applyFilter = (confirm) => {
    const filters = { ...finalWhereFilters, ...whereFilter };
    const pagination = { ...paginationProp, current: 1 };
    setFinalWhereFilters(filters);
    setPaginationProp(pagination);
    fetchMediaList({ filters, pagination });
    if (confirm) confirm();
  };

  const resetFilter = (clearFilters, dataIndex, confirm) => {
    const filters = { ...finalWhereFilters, [dataIndex]: null };
    if (dataIndex === 'type') filters.subtype = null;
    const pagination = { ...paginationProp, current: 1 };
    setWhereFilter(filters);
    setFinalWhereFilters(filters);
    setPaginationProp(pagination);
    setFilterVisible(false);
    fetchMediaList({ filters, pagination });
    if (clearFilters) clearFilters();
    if (confirm) confirm();
  };

  useEffect(() => {
    if (!filterVisible) {
      setWhereFilter(finalWhereFilters);
    }
  }, [filterVisible]);

  const onRadioChange = (value, dataIndex) => {
    const filtersCopy = { ...whereFilter, [dataIndex]: value };
    if (dataIndex === 'type') filtersCopy.subtype = null;
    setWhereFilter(filtersCopy);
  };

  const onRangePickerChange = (values, dataIndex) => {
    setWhereFilter({
      ...whereFilter,
      [dataIndex]: {
        from: values?.[0]?.startOf('day'),
        to: values?.[1]?.endOf('day'),
      },
    });
  };

  const getSubtypeFilterOptions = () =>
    whereFilter?.type
      ? getSubtypeOptions(whereFilter?.type)
      : allSubtypeOptions;

  const getFilterOptions = (dataIndex) => {
    switch (dataIndex) {
      case 'type':
        return typeOptions;
      case 'subtype':
        return getSubtypeFilterOptions();
      case 'fileType':
        return fileTypeOptions;
      default:
        return [];
    }
  };

  const filterPopup = (dataIndex) => ({
    filterDropdown: ({ confirm, clearFilters }) => (
      <div className="custom-filter-dropdown">
        {dataIndex === 'createdAt' ? (
          <RangePicker
            allowClear={false}
            format={DATE_FORMATS?.REGULAR}
            onChange={(values) => onRangePickerChange(values, dataIndex)}
            value={
              whereFilter?.[dataIndex]?.from
                ? [whereFilter?.[dataIndex]?.from, whereFilter?.[dataIndex]?.to]
                : null
            }
          />
        ) : (
          <Group
            options={getFilterOptions(dataIndex)}
            value={whereFilter?.[dataIndex]}
            onChange={(e) => onRadioChange(e?.target?.value, dataIndex)}
          />
        )}
        <Divider className="divider-filter" />
        <div className="d-flex justify-center">
          <Button
            size="small"
            className="reset-button"
            onClick={() => resetFilter(clearFilters, dataIndex, confirm)}
          >
            Reset
          </Button>
          <Button
            size="small"
            className="common-button ok-button"
            type="primary"
            onClick={() => applyFilter(confirm)}
          >
            Ok
          </Button>
        </div>
      </div>
    ),
    filterIcon: () =>
      !isEmpty(finalWhereFilters?.[dataIndex]) ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: (isVisible) => setFilterVisible(isVisible),
  });

  const columns = [
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 220,
      align: 'left',
      sorter: true,
      className: 'max-width-column table-tooltip-cell',
      // eslint-disable-next-line no-undef
      fixed: window?.innerWidth > BREAKPOINTS?.desktop ? 'left' : false,
      render: (_, record) => (
        <Tooltip
          title={record?.originalFileName || record?.name}
          placement="topLeft"
        >
          {record?.name || '-'}
        </Tooltip>
      ),
    },
    {
      title: 'TYPE',
      dataIndex: 'type',
      key: 'type',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => typeLabels?.[record?.type] || record?.type || '-',
      ...filterPopup('type'),
    },
    {
      title: 'SUBTYPE',
      dataIndex: 'subtype',
      key: 'subtype',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        subtypeLabels?.[record?.subtype] || record?.subtype || '-',
      ...filterPopup('subtype'),
    },
    {
      title: 'FILE TYPE',
      dataIndex: 'fileType',
      key: 'fileType',
      ellipsis: true,
      width: 140,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        fileTypeLabels?.[record?.fileType] || record?.fileType || '-',
      ...filterPopup('fileType'),
    },
    {
      title: 'SIZE',
      dataIndex: 'size',
      key: 'size',
      width: 110,
      align: 'left',
      sorter: true,
      className: 'max-width-column',
      render: (_, record) => formatFileSize(record?.size),
    },
    {
      title: 'PREVIEW',
      dataIndex: 'preview',
      key: 'preview',
      width: 90,
      align: 'center',
      render: (_, record) =>
        record?.url ? (
          <Tooltip title="View media" placement="top" zIndex={4}>
            <ViewIcon
              className="pointer svg-icon"
              onClick={() =>
                // eslint-disable-next-line no-undef
                window?.open(record?.url, '_blank', 'noopener,noreferrer')
              }
            />
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: 'CREATED AT',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      ellipsis: true,
      align: 'left',
      sorter: true,
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.createdAt, DATE_FORMATS?.REGULAR) || '-',
      ...filterPopup('createdAt'),
    },
    {
      title: 'ACTIONS',
      dataIndex: 'actions',
      key: 'actions',
      width: 130,
      align: 'center',
      // eslint-disable-next-line no-undef
      fixed: window?.innerWidth > BREAKPOINTS?.desktop ? 'right' : false,
      render: (_, record) => (
        <div className="action-button">
          <Tooltip title="Copy link" placement="top" zIndex={4}>
            <CopyLinkIcon
              className="mr-16 pointer svg-icon"
              onClick={(e) => copyToClipboard(e, record?.url)}
            />
          </Tooltip>
          {hasUpdatePermission && (
            <>
              <Tooltip title="Edit" placement="top" zIndex={4}>
                <EditIcon
                  className="mr-16 pointer svg-icon"
                  onClick={() => {
                    setMediaData(record);
                    setIsEditModalOpen(true);
                  }}
                />
              </Tooltip>
              <Tooltip
                title={isArchiveFilter ? 'Unarchive' : 'Archive'}
                placement="top"
                zIndex={4}
              >
                {isArchiveFilter ? (
                  <UnarchiveIcon
                    className="pointer svg-icon"
                    onClick={() => {
                      setMediaData(record);
                      setIsArchivePrompt(true);
                    }}
                  />
                ) : (
                  <ArchiveIcon
                    className="pointer svg-icon"
                    onClick={() => {
                      setMediaData(record);
                      setIsArchivePrompt(true);
                    }}
                  />
                )}
              </Tooltip>
            </>
          )}
        </div>
      ),
    },
  ]?.filter((item) => !item?.hidden);

  return (
    <>
      <UploadMediaModal
        isModalOpen={isUploadModalOpen}
        setIsModalOpen={setIsUploadModalOpen}
        onUploaded={() => {
          const pagination = { ...paginationProp, current: 1 };
          setPaginationProp(pagination);
          fetchMediaList({ pagination });
        }}
        typeOptions={typeOptions}
        getSubtypeOptions={getSubtypeOptions}
        isOptionsLoading={isOptionsLoading}
      />
      <EditMediaModal
        isModalOpen={isEditModalOpen}
        setIsModalOpen={setIsEditModalOpen}
        mediaData={mediaData}
        onFinish={handleEdit}
        isBtnLoading={isEditBtnLoading}
        typeOptions={typeOptions}
        getSubtypeOptions={getSubtypeOptions}
        isOptionsLoading={isOptionsLoading}
      />
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isArchivePrompt}
        onOk={handleArchive}
        onCancel={() => setIsArchivePrompt(false)}
        okButtonProps={{ loading: isArchiveBtnLoading }}
        cancelButtonProps={{ disabled: isArchiveBtnLoading }}
      >
        Are you sure you want to
        <strong>
          {' '}
          {!mediaData?.isArchived
            ? ARCHIVING_STATE?.ARCHIVED
            : ARCHIVING_STATE?.UNARCHIVED}{' '}
        </strong>
        this media?
      </Modal>
      <div className="d-flex justify-end mb-16 flex-wrap">
        <div className="mr-16 d-flex align-center">
          <h4 className="m-0 mr-8">Only Archived</h4>
          <Switch
            size="small"
            checked={isArchiveFilter}
            onChange={(checked) => setIsArchiveFilter(checked)}
          />
        </div>
        <SearchComponent
          className="search-box"
          query={query}
          setQuery={setQuery}
          getData={handleSearch}
        />
      </div>
      <CommonTable
        columns={columns}
        data={mediaList || []}
        loadingData={isMediaLoading}
        onChange={handleTableChange}
        paginationConfig={paginationProp}
        rowKey={(record) => record?.id}
      />
    </>
  );
};

export default MediaLibraryTable;
